import "~/misc/common";
import L from "./leaflet"; // your shared leaflet module

// ------------------------------
// Helper functions
// ------------------------------

export const xy = (x: { x: number; y: number; } | [number, number] | number, y?: number): L.LatLng => {
  // Case 1: object with x and y
  if (typeof x === "object" && "x" in x && "y" in x) {
    return L.latLng(x.y, x.x);
  }
  // Case 2: array [lng, lat]
  if (Array.isArray(x)) {
    return L.latLng(x[1], x[0]);
  }
  // Case 3: two numbers
  return L.latLng(y!, x as number);
};

export const xy_poly = (poly: any[]): L.LatLng[] => poly.map(p => xy(p));

// ------------------------------
// Extend Leaflet classes
// ------------------------------

declare module "leaflet" {
  interface LatLng {
    toXY(): { x: number; y: number; };
  }
  interface Map {
    myFlyTo(latlng: L.LatLngExpression, zoom: number,
      options?: L.ZoomPanOptions & { maxDistance?: number; maxZoomDiff?: number; }): void;
  }
}

L.LatLng.prototype.toXY = function () {
  return { x: this.lng, y: this.lat };
};

L.Map.include({
  myFlyTo(latlng: L.LatLngExpression, zoom: number, options: any = {}) {
    const currentCenter = this.getCenter();
    const currentZoom = this.getZoom();

    const distance = currentCenter.distanceTo(L.latLng(latlng));
    const zoomDiff = Math.abs(currentZoom - zoom);

    const maxDistance = options.maxDistance ?? 10;
    const maxZoomDiff = options.maxZoomDiff ?? 0.1;

    if (distance < maxDistance && zoomDiff < maxZoomDiff) {
      console.log("myFlyTo skipped — view is already close enough.");
      return;
    }

    return this.flyTo(latlng, zoom, options);
  },
});

// ------------------------------
// DOM helpers
// ------------------------------

export const refreshControlPanelMaxHeight = (selector = "#mapol") => {
  const refresh = () => {
    const mapHeight = $(selector).height();
    if (mapHeight) {
      $(".leaflet-control-layers").css("max-height", mapHeight - 100);
      $(".panel-fixed").css("max-height", mapHeight - 100);
    }
  };

  $(window).resize(refresh);
  refresh();
};

// ------------------------------
// Geometry utilities
// ------------------------------

export const rect = (x1: number, y1: number, x2: number, y2: number): [number, number][] => [
  [x1, y1],
  [x2, y1],
  [x2, y2],
  [x1, y2],
];

export const line_round = (x1: number, y1: number, x2: number, y2: number, r: number): [number, number][] => {
  const segments = 30;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lineLength = Math.sqrt(dx * dx + dy * dy);
  const unitDX = dx / lineLength;
  const unitDY = dy / lineLength;
  const normalX = -unitDY;
  const normalY = unitDX;

  const generateArc = (centerX: number, centerY: number, startAngle: number, endAngle: number) => {
    const arcPoints: [number, number][] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + ((endAngle - startAngle) * i) / segments;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      arcPoints.push([x, y]);
    }
    return arcPoints;
  };

  const startAngle = Math.atan2(dy, dx) + Math.PI / 2;
  const endAngle = Math.atan2(dy, dx) - Math.PI / 2;

  const startArc = generateArc(x1, y1, startAngle, startAngle + Math.PI);
  const endArc = generateArc(x2, y2, endAngle, endAngle + Math.PI);

  return [...startArc, ...endArc];
};
